import {BalloonEventModel} from 'ts-core';
import {useBalloonEventsData} from '../contexts/EventData';
import {useEffect, useRef, useState, useCallback} from "react";
import {useEventsListContext} from '../contexts/EventsListContext';

/**
 * Hook for managing event interaction states (hover, select, click)
 * Provides consistent interaction behavior across all event display components
 */
export function useEventInteraction(event: BalloonEventModel) {
    const {
        hoveredEventId,
        setHoveredEventId,
        selectEvent,
        selectedEvent,
        deselectEvent,
        openedEvent
    } = useBalloonEventsData();
    
    const { containerRef } = useEventsListContext();
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const hasScrolledForCurrentSelection = useRef(false);
    
    // Reset scroll flag when a new event is selected
    useEffect(() => {
        hasScrolledForCurrentSelection.current = false;
    }, [selectedEvent?.id]);
    
    // Check visibility when selected event changes
    useEffect(() => {
        if (!ref.current || !containerRef.current) return;
        
        const element = ref.current;
        const container = containerRef.current;
        
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            {
                root: container,
                threshold: 0.1,
            }
        );
        
        observer.observe(element);
        
        return () => observer.disconnect();
    }, [containerRef, ref]);
    
    // Scroll into view when this event is selected and not visible (only once per selection)
    // Uses requestAnimationFrame to ensure DOM is settled after events list re-render
    useEffect(() => {
        if (
            selectedEvent?.id === event.id && 
            ref.current && 
            containerRef.current &&
            !hasScrolledForCurrentSelection.current
        ) {
            // Defer to next frame so the IntersectionObserver has time to report visibility.
            // If the element is already visible, scrollIntoView with block:'nearest' is a no-op.
            requestAnimationFrame(() => {
                if (ref.current && !hasScrolledForCurrentSelection.current) {
                    ref.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                    });
                    hasScrolledForCurrentSelection.current = true;
                }
            });
        }
    }, [selectedEvent?.id, event.id, containerRef]);

    const hovered = hoveredEventId === event.id;
    const selected = selectedEvent?.id === event.id;
    const isOpened = openedEvent?.id === event.id;

    // Memoize event handlers to prevent unnecessary re-renders
    const handleMouseEnter = useCallback(() => {
        setHoveredEventId(event.id);
    }, [event.id, setHoveredEventId]);

    const handleMouseLeave = useCallback(() => {
        setHoveredEventId('');
    }, [setHoveredEventId]);

    const handleClick = useCallback(() => {
        if (isOpened) {
            deselectEvent();
        } else {
            selectEvent(event.id);
        }
    }, [isOpened, deselectEvent, selectEvent, event.id]);

    return {
        hovered,
        selected,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onClick: handleClick,
        eventRef: ref,
    };
}
